import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import HelpBubble from "./components/HelpBubble";
import Footer from "./components/Footer";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import NewDeck from "./pages/NewDeck";
import DeckView from "./pages/DeckView";
import StudySession from "./pages/StudySession";
import PublicDeckView from "./pages/PublicDeckView";
import Admin from "./pages/Admin";
import Profile from "./pages/Profile";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import DocumentVault from "./pages/DocumentVault";
import Help from "./pages/Help";
import Legal from "./pages/Legal";
import TeacherClasses from "./pages/TeacherClasses";
import ClassDetail from "./pages/ClassDetail";
import { Toaster } from "sonner";
import { cancelSpeech } from "./lib/speech";

function AppRouter() {
  const location = useLocation();

  // Safety net: every time the user navigates AWAY from a study route, hard-cancel
  // any in-flight TTS. The per-page unmount cleanup SHOULD handle this, but the
  // Web Speech API's queue is global — if a setTimeout slipped through the cancel
  // window, the reader can keep going after the component is gone. Guarantees
  // that navigating to /dashboard, /profile, or logging out always silences Ekkol.
  useEffect(() => {
    const onStudyNow = /^\/study\//.test(location.pathname);
    if (!onStudyNow) {
      try { cancelSpeech(); } catch {}
    }
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Hide the help bubble during a study session (full-screen voice flow)
  const onStudy = /^\/study\//.test(location.pathname);
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/help" element={<Help />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/decks/new" element={<ProtectedRoute><NewDeck /></ProtectedRoute>} />
        <Route path="/decks/:deckId" element={<ProtectedRoute><DeckView /></ProtectedRoute>} />
        <Route path="/study/:deckId" element={<ProtectedRoute><StudySession /></ProtectedRoute>} />
        <Route path="/classes" element={<ProtectedRoute><TeacherClasses /></ProtectedRoute>} />
        <Route path="/classes/:classId" element={<ProtectedRoute><ClassDetail /></ProtectedRoute>} />
        <Route path="/d/:deckId" element={<PublicDeckView />} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/admin/vault" element={<ProtectedRoute><DocumentVault /></ProtectedRoute>} />
        <Route path="*" element={<Landing />} />
      </Routes>
      {!onStudy && <Footer />}
      {!onStudy && <HelpBubble />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
